/**
 * Deactivated Team Guard
 *
 * Blocks access to the app when a team has been deactivated after subscription cancellation.
 * Shows a message explaining the situation and provides contact info for support.
 *
 * This guard should be placed inside TeamProvider but before main app routes.
 */

import { Loader2 } from 'lucide-react';
import { useTeam } from '@/contexts/TeamContext';
import { TeamDeactivatedScreen } from '@/components/screens/TeamDeactivatedScreen';

interface DeactivatedTeamGuardProps {
  children: React.ReactNode;
}

export function DeactivatedTeamGuard({ children }: DeactivatedTeamGuardProps) {
  const { isDeactivated, team, loading } = useTeam();

  // Show neutral loader while team doc is resolving — prevents dashboard flash
  // before deactivation check completes (contract §3: no UI flash before redirect)
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // If team is not deactivated, render children normally
  if (!isDeactivated) {
    return <>{children}</>;
  }

  // Team is deactivated — delegate to canonical TeamDeactivatedScreen
  return <TeamDeactivatedScreen teamName={team?.name} />;
}

export default DeactivatedTeamGuard;
